import { Flex, Text } from '@radix-ui/themes';
import { DiamondCanvas } from 'classes/diamond-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import './diamond-view.scss';

const REDRAW_DEQUEUE_MS = 100;

interface IProps {
  hitter: Nullable<string> | undefined;
  hitDetails: Partial<ISHHitDetails> | undefined;
}

// ! deprecate this and use the new one in Analytics 2.0 instead
export const HitRecapDiamond = (props: IProps) => {
  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const fieldCanvas = useRef<HTMLCanvasElement>(null);
  const hitCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();

  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(() => {
    const output = DiamondCanvas.makeWidget({
      isDark: isDarkMode,
      ftToPx: 1,
      minXFt: -300,
      maxXFt: 300,
      minYFt: -25,
      maxYFt: 500,
      widthScale: 2,
    });

    return output;
  }, [isDarkMode]);

  // whenever a new hit is reported, redraw things
  useEffect(() => {
    setLastUpdate(Date.now());
  }, [canvasDef, props.hitDetails]);

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [fieldCanvas.current, hitCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawField
  useEffect(() => {
    try {
      const canvas = fieldCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      canvasDef.drawField(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef]);

  // drawHit
  useEffect(() => {
    try {
      const canvas = hitCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!props.hitDetails) {
        return;
      }

      if (props.hitDetails.distanceFT === undefined) {
        return;
      }

      if (props.hitDetails.hLaunchDEG === undefined) {
        return;
      }

      canvasDef.drawHit(ctx, props.hitDetails);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef, props.hitDetails]);

  const outcome = useMemo(() => {
    const code =
      props.hitDetails?._mx_outcome ||
      HitHelper.getOutcomeLegacy(props.hitDetails ?? {});

    if (code === OutcomeCode.Unknown) {
      // show nothing
      return;
    }

    return {
      label: HitHelper.getOutcomeLabel(code),
      color: HitHelper.getOutcomeColor(code, canvasDef.CONFIG.colors.default),
    };
  }, [props.hitDetails]);

  return (
    <ErrorBoundary identifier="DiamondView">
      <div
        className="DiamondWidget"
        style={{ aspectRatio: canvasDef.CONFIG.canvas.aspectRatio }}
      >
        {/* for log rocket recordings to show the results */}
        {imgSrc && <img src={imgSrc} />}
        <canvas
          ref={mergeCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={fieldCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={hitCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
      </div>

      <Flex gap="1" align="center" justify="center" mb="2">
        <Text>
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              display: 'inline-block',
              backgroundColor: outcome?.color,
              marginBottom: '2px',
            }}
          />
        </Text>
        {/* draw placeholder to reserve the vertical space */}
        <Text title={props.hitter ?? undefined}>{outcome?.label ?? ' '}</Text>
      </Flex>
    </ErrorBoundary>
  );
};
